import { openDB, type IDBPDatabase, type DBSchema } from 'idb';

/**
 * Offline read cache — Layer 2 of offline support.
 *
 * Strategy: a thin per-table store in IndexedDB, written through whenever
 * the corresponding lib/db.ts function reads from the network. When offline
 * (or when the network read fails), reads fall back to the cache.
 *
 * This is intentionally NOT a full local replica — we don't sync down
 * everything ever. We cache the data the user has actually looked at,
 * scoped per-org so a session switch doesn't poison another org's cache.
 *
 * Layer 3 (write outbox) lives in a separate file and adds the ability
 * to *create* records offline. This file is read-only.
 */

// ============================================================
// Schema
// ============================================================
// Every record in every store is wrapped: { key, orgId, updatedAt, value }
// - key: the primary key in the source table (a uuid in 99% of cases)
// - orgId: which organisation this record belongs to. Allows clearCache(org)
//   on sign-out + protects against cross-org leaks when a user re-logs in.
// - updatedAt: when we cached it (ms epoch). Lets us show freshness in UI.
// - value: the row as returned by Supabase, unchanged.

interface CacheEntry<V = unknown> {
  key:        string;
  orgId:      string;
  updatedAt:  number;
  value:      V;
}

// All hot-table store names live here, so adding one is a one-line change
// plus a bump to DB_VERSION below.
export const CACHE_STORES = [
  'sites',
  'sign_ins',
  'rams',
  'coshh',
  'training',
  'plant',
  'subcontractors',
  'forms',
  'documents',
  'org',           // singleton: the current organisation row
] as const;
export type CacheStore = typeof CACHE_STORES[number];

// Bump this when you change the schema. idb runs the upgrade callback on
// every version increase.
const DB_NAME    = 'safe_and_sorted_cache';
const DB_VERSION = 1;

interface CacheSchema extends DBSchema {
  sites:          { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  sign_ins:       { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  rams:           { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  coshh:          { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  training:       { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  plant:          { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  subcontractors: { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  forms:          { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  documents:      { key: string; value: CacheEntry; indexes: { 'by-org': string } };
  org:            { key: string; value: CacheEntry; indexes: { 'by-org': string } };
}

let _dbPromise: Promise<IDBPDatabase<CacheSchema>> | null = null;

function db(): Promise<IDBPDatabase<CacheSchema>> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = openDB<CacheSchema>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      for (const name of CACHE_STORES) {
        if (!database.objectStoreNames.contains(name)) {
          const store = database.createObjectStore(name, { keyPath: 'key' });
          store.createIndex('by-org', 'orgId', { unique: false });
        }
      }
    },
  });
  return _dbPromise;
}

// ============================================================
// Public API
// ============================================================

/** Write a single record. Idempotent — replaces any existing entry for that key. */
export async function cachePut<V>(
  store: CacheStore,
  orgId: string,
  key:   string,
  value: V,
): Promise<void> {
  try {
    const d = await db();
    await d.put(store, { key, orgId, updatedAt: Date.now(), value: value as unknown });
  } catch (e) {
    // Cache writes must never break the app — they're a perf/UX win, not load-bearing.
    console.warn('[cache] put failed:', store, e);
  }
}

/** Write many rows at once — used by list-returning lib/db.ts wrappers. */
export async function cachePutMany<V>(
  store: CacheStore,
  orgId: string,
  rows:  Array<{ id: string } & V>,
): Promise<void> {
  if (rows.length === 0) return;
  try {
    const d  = await db();
    const tx = d.transaction(store, 'readwrite');
    await Promise.all(
      rows.map((row) =>
        tx.store.put({ key: row.id, orgId, updatedAt: Date.now(), value: row as unknown }),
      ),
    );
    await tx.done;
  } catch (e) {
    console.warn('[cache] putMany failed:', store, e);
  }
}

/** Read a single record by primary key. Returns undefined if not cached. */
export async function cacheGet<V>(
  store: CacheStore,
  key:   string,
): Promise<V | undefined> {
  try {
    const d = await db();
    const entry = await d.get(store, key);
    return entry ? (entry.value as V) : undefined;
  } catch (e) {
    console.warn('[cache] get failed:', store, e);
    return undefined;
  }
}

/** List all cached rows for a given organisation. Caller does filtering. */
export async function cacheListByOrg<V>(
  store: CacheStore,
  orgId: string,
): Promise<V[]> {
  try {
    const d = await db();
    const entries = await d.getAllFromIndex(store, 'by-org', orgId);
    return entries.map((e) => e.value as V);
  } catch (e) {
    console.warn('[cache] listByOrg failed:', store, e);
    return [];
  }
}

/** Drop everything for one org — call on sign-out so the next user
 *  doesn't accidentally see the previous user's cached data. */
export async function clearCacheForOrg(orgId: string): Promise<void> {
  try {
    const d = await db();
    const tx = d.transaction(CACHE_STORES, 'readwrite');
    for (const name of CACHE_STORES) {
      const idx = tx.objectStore(name).index('by-org');
      let cursor = await idx.openCursor(orgId);
      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }
    }
    await tx.done;
  } catch (e) {
    console.warn('[cache] clearForOrg failed:', e);
  }
}

/** Drop EVERYTHING — used by the "Reset cache" debug tool. */
export async function clearAllCache(): Promise<void> {
  try {
    const d = await db();
    const tx = d.transaction(CACHE_STORES, 'readwrite');
    for (const name of CACHE_STORES) {
      await tx.objectStore(name).clear();
    }
    await tx.done;
  } catch (e) {
    console.warn('[cache] clearAll failed:', e);
  }
}

/** Convenience: should we even try the network right now? */
export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

/**
 * Wrap a network read with cache fallback. Returns network result if online,
 * cached result if offline / network fails. Writes back to cache on success.
 *
 * Use case:
 *   const sites = await readThroughCache(
 *     'sites', org.org_id,
 *     () => listSitesNetwork(org.org_id),
 *   );
 */
export async function readThroughCache<T extends { id: string }>(
  store: CacheStore,
  orgId: string,
  fetcher: () => Promise<T[]>,
): Promise<T[]> {
  if (!isOnline()) {
    return cacheListByOrg<T>(store, orgId);
  }
  try {
    const fresh = await fetcher();
    // Fire-and-forget write-through. Don't block the UI on cache writes.
    void cachePutMany<T>(store, orgId, fresh);
    return fresh;
  } catch (e) {
    // Network error mid-flight — fall back to cache so the user still sees
    // *something*. Log so it's traceable in dev.
    console.warn('[cache] network read failed, falling back:', store, e);
    return cacheListByOrg<T>(store, orgId);
  }
}
